/**
 * Value Chain Risk V3 - Risk Intelligence Layer
 * 
 * This is NOT a "supplier dashboard." It is a Legal Exposure Intake Center.
 * 
 * Purpose:
 * - Cross-entity supplier concentration risk
 * - Cross-tier risk amplification
 * - Multi-impact clustering
 * - Exposure propagation
 * 
 * Architecture:
 * [ Defensibility Flywheel - Horizontal Process Layer ]
 * [ Global Context Bar ]
 * [ Systemic Exposure KPI Strip ]
 * [ Filter Panel + Controls ]
 * [ Dual View: Signals Registry | Supplier Risk Grid ]
 * [ Supplier/Impact Detail Drawer ]
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  AlertTriangle, Shield, Activity, ChevronRight, ChevronDown, ArrowRight, X,
  ExternalLink, FileText, Clock, CheckCircle2, XCircle, AlertCircle,
  Users, Building2, Globe, Scale, TrendingUp, TrendingDown, Filter,
  Search, RefreshCw, Download, Link2, Layers, Target, Zap, Eye, Radio,
  MoreVertical, Upload, MapPin, Factory, Hash, Package, Lock
} from 'lucide-react';
import { useEventRouter } from '../hooks/useEventRouter';
import { can, getPrimaryRole, getSnapshotLockState } from '../lib/permissions';
import { PermissionGate, SnapshotLockBanner } from '../components/PermissionGate';
import DefensibilityFlywheel from '../components/DefensibilityFlywheel';
import { apiFetch } from '../services/api_service';


// =============================================================================
// CONSTANTS
// =============================================================================

const SEVERITY_CONFIG = {
  5: { label: 'Critical', color: 'red', weight: 5 },
  4: { label: 'High', color: 'orange', weight: 4 },
  3: { label: 'Medium', color: 'amber', weight: 3 },
  2: { label: 'Low', color: 'emerald', weight: 2 },
  1: { label: 'Minimal', color: 'slate', weight: 1 }
};

const CLASSIFICATION_CONFIG = {
  human_rights: { label: 'Human Rights', color: 'rose', icon: Users },
  environment: { label: 'Environment', color: 'emerald', icon: Globe },
  governance: { label: 'Governance', color: 'blue', icon: Building2 },
  climate: { label: 'Climate', color: 'cyan', icon: Activity },
  labor: { label: 'Labor', color: 'purple', icon: Users }
};

const RISK_LEVEL_CONFIG = {
  HIGH: { label: 'High Risk', color: 'red' },
  MEDIUM: { label: 'Medium Risk', color: 'amber' },
  LOW: { label: 'Low Risk', color: 'emerald' }
};

// =============================================================================
// KPI STRIP COMPONENT
// =============================================================================

const SystemicKPIStrip = ({ signalStats, supplierStats, onKpiClick }) => {
  const kpis = [
    { key: 'totalSignals', label: 'Total Signals', value: signalStats.totalSignals || 0, color: 'blue' },
    { key: 'criticalSignals', label: 'Critical Signals', value: signalStats.criticalCount || 0, color: 'red' },
    { key: 'unresolved', label: 'Unresolved', value: signalStats.unresolvedCount || 0, color: 'amber' },
    { key: 'totalSuppliers', label: 'Suppliers', value: supplierStats.totalSuppliers || 0, color: 'emerald' },
    { key: 'highRisk', label: 'High Risk Suppliers', value: supplierStats.highRiskCount || 0, color: 'red' },
    { key: 'assessed', label: 'Assessed', value: supplierStats.assessedCount || 0, color: 'blue' }
  ];

  return (
    <div className="grid grid-cols-6 gap-3" data-testid="systemic-kpi-strip">
      {kpis.map(kpi => (
        <button
          key={kpi.key}
          onClick={() => onKpiClick?.(kpi.key)}
          className={`p-3 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg hover:border-${kpi.color}-500/50 transition-colors text-left`}
          data-testid={`kpi-${kpi.key}`}
        >
          <p className="text-xs text-[#6B6B6B] mb-1">{kpi.label}</p>
          <p className={`text-xl font-bold text-${kpi.color}-400`}>{kpi.value}</p>
        </button>
      ))}
    </div>
  );
};

// =============================================================================
// FILTER PANEL COMPONENT
// =============================================================================

const FilterPanel = ({ filters, onFilterChange, activeView, onViewChange }) => {
  return (
    <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-4" data-testid="filter-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-[#6B6B6B]" />
          <span className="text-sm text-white font-medium">Filters</span>
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-[#0F0F0F] rounded-lg p-1">
          <button
            onClick={() => onViewChange('signals')}
            className={`px-3 py-1.5 text-xs rounded ${activeView === 'signals' 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'text-[#6B6B6B] hover:text-white'}`}
          >
            Risk Signals
          </button>
          <button
            onClick={() => onViewChange('suppliers')}
            className={`px-3 py-1.5 text-xs rounded ${activeView === 'suppliers' 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'text-[#6B6B6B] hover:text-white'}`}
          >
            Supplier Registry
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input 
            type="text"
            placeholder="Search signals or suppliers..."
            className="w-full pl-9 pr-4 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-white placeholder-[#4F4F4F] focus:border-emerald-500/50 focus:outline-none"
            value={filters.search || ''}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
            data-testid="search-input"
          />
        </div>
        
        <select 
          className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
          value={filters.severity || ''}
          onChange={(e) => onFilterChange({ ...filters, severity: e.target.value })}
        >
          <option value="">All Severity</option>
          {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        
        <select 
          className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
          value={filters.classification || ''}
          onChange={(e) => onFilterChange({ ...filters, classification: e.target.value })}
        >
          <option value="">All Classifications</option>
          {Object.entries(CLASSIFICATION_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>

        <select 
          className="px-3 py-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded text-sm text-[#A0A0A0]"
          value={filters.riskLevel || ''}
          onChange={(e) => onFilterChange({ ...filters, riskLevel: e.target.value })}
        >
          <option value="">All Risk Levels</option>
          {Object.entries(RISK_LEVEL_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>

        <button 
          onClick={() => onFilterChange({})}
          className="px-3 py-2 text-xs text-[#6B6B6B] hover:text-white"
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// SIGNALS REGISTRY GRID
// =============================================================================

const SignalsRegistryGrid = ({ signals, loading, onRowClick }) => {
  const columns = [
    { key: 'id', label: 'Signal ID', width: 'w-[12%]' },
    { key: 'supplierName', label: 'Supplier', width: 'w-[15%]' },
    { key: 'signalType', label: 'Type', width: 'w-[10%]' },
    { key: 'classification', label: 'Classification', width: 'w-[12%]' },
    { key: 'severity', label: 'Severity', width: 'w-[10%]' },
    { key: 'status', label: 'Status', width: 'w-[10%]' },
    { key: 'source', label: 'Source', width: 'w-[10%]' },
    { key: 'detectedAt', label: 'Detected', width: 'w-[12%]' },
    { key: 'actions', label: '', width: 'w-[5%]' }
  ];

  return (
    <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg overflow-hidden" data-testid="signals-registry">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F] bg-[#0F0F0F]">
              {columns.map(col => (
                <th key={col.key} className={`px-3 py-3 text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium ${col.width}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-[#6B6B6B]">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading signals...
                </td>
              </tr>
            ) : signals.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-[#6B6B6B]">
                  No risk signals found
                </td>
              </tr>
            ) : signals.map(signal => {
              const severityCfg = SEVERITY_CONFIG[signal.severity] || SEVERITY_CONFIG[3];
              const classCfg = CLASSIFICATION_CONFIG[signal.classification] || CLASSIFICATION_CONFIG.human_rights;
              const ClassIcon = classCfg.icon;

              return (
                <tr 
                  key={signal.id}
                  className="border-b border-[#1F1F1F] hover:bg-[#0F0F0F] cursor-pointer transition-colors"
                  onClick={() => onRowClick?.(signal)}
                  data-testid={`signal-row-${signal.id}`}
                >
                  <td className="px-3 py-3">
                    <span className="font-mono text-xs text-white">{signal.id?.slice(0, 12)}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#A0A0A0]">{signal.supplierName || 'Unknown'}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      signal.signalType === 'actual' 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {signal.signalType?.toUpperCase() || 'POTENTIAL'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      <ClassIcon className={`w-3 h-3 text-${classCfg.color}-400`} />
                      <span className="text-xs text-[#A0A0A0]">{classCfg.label}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded bg-${severityCfg.color}-500/20 text-${severityCfg.color}-400`}>
                      {severityCfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      signal.status === 'resolved' 
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : signal.status === 'dismissed'
                          ? 'bg-slate-500/20 text-slate-400'
                          : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {signal.status || 'Open'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#6B6B6B]">{signal.source || 'Manual'}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#6B6B6B]">
                      {signal.detectedAt ? new Date(signal.detectedAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <button className="p-1 hover:bg-[#1F1F1F] rounded">
                      <MoreVertical className="w-4 h-4 text-[#6B6B6B]" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =============================================================================
// SUPPLIER RISK GRID
// =============================================================================

const SupplierRiskGrid = ({ suppliers, loading, onRowClick }) => {
  const columns = [
    { key: 'name', label: 'Supplier', width: 'w-[18%]' },
    { key: 'tier', label: 'Tier', width: 'w-[8%]' },
    { key: 'country', label: 'Country', width: 'w-[10%]' },
    { key: 'riskScore', label: 'Risk Score', width: 'w-[10%]' },
    { key: 'riskLevel', label: 'Risk Level', width: 'w-[10%]' },
    { key: 'signalCount', label: 'Signals', width: 'w-[8%]' },
    { key: 'impactCount', label: 'Impacts', width: 'w-[8%]' },
    { key: 'lastAssessedAt', label: 'Last Assessed', width: 'w-[12%]' },
    { key: 'actions', label: '', width: 'w-[5%]' }
  ];

  return (
    <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg overflow-hidden" data-testid="supplier-risk-grid">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F] bg-[#0F0F0F]">
              {columns.map(col => (
                <th key={col.key} className={`px-3 py-3 text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium ${col.width}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-[#6B6B6B]">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading suppliers...
                </td>
              </tr>
            ) : suppliers.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-[#6B6B6B]">
                  No suppliers found
                </td>
              </tr>
            ) : suppliers.map(supplier => {
              const riskCfg = RISK_LEVEL_CONFIG[supplier.riskLevel] || RISK_LEVEL_CONFIG.LOW;

              return (
                <tr 
                  key={supplier.id}
                  className={`border-b border-[#1F1F1F] hover:bg-[#0F0F0F] cursor-pointer transition-colors
                    ${supplier.riskLevel === 'HIGH' ? 'bg-red-500/5' : ''}`}
                  onClick={() => onRowClick?.(supplier)}
                  data-testid={`supplier-row-${supplier.id}`}
                >
                  <td className="px-3 py-3">
                    <div>
                      <span className="text-sm text-white font-medium">{supplier.name}</span>
                      <span className="block text-xs text-[#6B6B6B] font-mono">{supplier.id?.slice(0, 12)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="px-2 py-0.5 text-xs rounded bg-blue-500/20 text-blue-400">
                      T{supplier.tier}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#A0A0A0]">{supplier.country || 'N/A'}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-sm font-bold text-white">{(supplier.riskScore * 100).toFixed(0)}%</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded bg-${riskCfg.color}-500/20 text-${riskCfg.color}-400`}>
                      {riskCfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs ${supplier.signalCount > 0 ? 'text-amber-400' : 'text-[#6B6B6B]'}`}>
                      {supplier.signalCount || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs ${supplier.impactCount > 0 ? 'text-red-400' : 'text-[#6B6B6B]'}`}>
                      {supplier.impactCount || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[#6B6B6B]">
                      {supplier.lastAssessedAt ? new Date(supplier.lastAssessedAt).toLocaleDateString() : 'Never'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <button className="p-1 hover:bg-[#1F1F1F] rounded">
                      <MoreVertical className="w-4 h-4 text-[#6B6B6B]" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =============================================================================
// DETAIL DRAWER COMPONENT
// =============================================================================

const DetailDrawer = ({ item, type, onClose }) => {
  if (!item) return null;

  const isSignal = type === 'signal';
  const title = isSignal ? 'Risk Signal Details' : 'Supplier Risk Profile';
  const Icon = isSignal ? AlertTriangle : Building2;

  return (
    <div className="fixed right-0 top-0 h-full w-[40%] bg-[#0A0A0A] border-l border-[#1F1F1F] shadow-2xl z-40 flex flex-col" data-testid="detail-drawer">
      {/* Header */}
      <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between bg-[#0F0F0F]">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
            <Icon className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-outfit font-semibold text-white">{title}</h3>
            <p className="text-xs text-[#6B6B6B] font-mono">{item.id || item.supplierId}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-[#1F1F1F] rounded">
          <X className="w-5 h-5 text-[#6B6B6B]" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isSignal ? (
          <>
            {/* Signal Overview */}
            <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
              <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Signal Overview</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#6B6B6B]">Type</p>
                  <p className={`text-sm font-bold ${item.signalType === 'actual' ? 'text-red-400' : 'text-amber-400'}`}>
                    {item.signalType?.toUpperCase() || 'POTENTIAL'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Severity</p>
                  <p className={`text-sm font-bold text-${SEVERITY_CONFIG[item.severity]?.color || 'amber'}-400`}>
                    {SEVERITY_CONFIG[item.severity]?.label || 'Medium'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Classification</p>
                  <p className="text-sm text-white">{CLASSIFICATION_CONFIG[item.classification]?.label || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Status</p>
                  <p className="text-sm text-white">{item.status || 'Open'}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            {item.description && (
              <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
                <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2">Description</p>
                <p className="text-sm text-[#A0A0A0]">{item.description}</p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Supplier Overview */}
            <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
              <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Supplier Profile</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#6B6B6B]">Name</p>
                  <p className="text-sm font-bold text-white">{item.name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Tier</p>
                  <p className="text-sm text-blue-400">Tier {item.tier}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Country</p>
                  <p className="text-sm text-white">{item.country || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Risk Score</p>
                  <p className={`text-sm font-bold text-${RISK_LEVEL_CONFIG[item.riskLevel]?.color || 'emerald'}-400`}>
                    {(item.riskScore * 100).toFixed(0)}%
                  </p>
                </div>
              </div>
            </div>

            {/* Risk Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <p className="text-xs text-[#6B6B6B]">Active Signals</p>
                <p className="text-lg font-bold text-amber-400">{item.signalCount || 0}</p>
              </div>
              <div className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
                <p className="text-xs text-[#6B6B6B]">Active Impacts</p>
                <p className="text-lg font-bold text-red-400">{item.impactCount || 0}</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT: VALUE CHAIN RISK V3
// =============================================================================

const ValueChainRiskV3 = () => {
  // Event Router
  const {
    readModels,
    refreshModel,
    refreshAll,
    mode,
    connectionStatus,
    isConnected
  } = useEventRouter({
    groupId: 'grp001',
    boundaryId: 'bnd001',
    period: 'FY2024'
  });

  // State
  const [signals, setSignals] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [signalStats, setSignalStats] = useState({});
  const [supplierStats, setSupplierStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [activeView, setActiveView] = useState('signals');
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerType, setDrawerType] = useState(null);

  // Snapshot Lock
  const snapshotLockState = getSnapshotLockState();
  const isSnapshotLocked = snapshotLockState?.isLocked || false;

  // Fetch signals
  const fetchSignals = useCallback(async () => {
    try {
      const data = await apiFetch(`/spine/v1/csddd/readmodels/value-chain/signals?groupId=grp001&boundaryId=bnd001&period=FY2024`);
      if (data && !data._error) {
        setSignals(data.signals || []);
        setSignalStats({
          totalSignals: data.totalSignals || 0,
          criticalCount: data.criticalCount || 0,
          unresolvedCount: data.unresolvedCount || 0
        });
      }
    } catch (err) {
      console.error('Error fetching signals:', err);
    }
  }, []);

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      const data = await apiFetch(`/spine/v1/csddd/readmodels/value-chain/suppliers?groupId=grp001&boundaryId=bnd001&period=FY2024`);
      if (data && !data._error) {
        setSuppliers(data.suppliers || []);
        setSupplierStats({
          totalSuppliers: data.totalSuppliers || 0,
          highRiskCount: data.highRiskCount || 0,
          assessedCount: data.assessedCount || 0
        });
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchSignals();
    fetchSuppliers();
  }, [fetchSignals, fetchSuppliers]);

  // Listen for event-driven updates
  useEffect(() => {
    const signalsModel = readModels.VALUE_CHAIN_RISK_SIGNALS;
    const suppliersModel = readModels.VALUE_CHAIN_RISK_SUPPLIERS;
    
    if (signalsModel?.data) {
      setSignals(signalsModel.data.signals || []);
      setSignalStats({
        totalSignals: signalsModel.data.totalSignals,
        criticalCount: signalsModel.data.criticalCount,
        unresolvedCount: signalsModel.data.unresolvedCount
      });
    }
    if (suppliersModel?.data) {
      setSuppliers(suppliersModel.data.suppliers || []);
      setSupplierStats({
        totalSuppliers: suppliersModel.data.totalSuppliers,
        highRiskCount: suppliersModel.data.highRiskCount,
        assessedCount: suppliersModel.data.assessedCount
      });
    }
  }, [readModels]);

  // Filter signals
  const filteredSignals = useMemo(() => {
    return signals.filter(s => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (!s.id?.toLowerCase().includes(search) && 
            !s.supplierName?.toLowerCase().includes(search)) {
          return false;
        }
      }
      if (filters.severity && s.severity !== parseInt(filters.severity)) return false;
      if (filters.classification && s.classification !== filters.classification) return false;
      
      // Map riskLevel to severity ranges for signals
      // HIGH = Critical (5) + High (4)
      // MEDIUM = Medium (3)
      // LOW = Low (2) + Minimal (1)
      if (filters.riskLevel) {
        if (filters.riskLevel === 'HIGH' && s.severity < 4) return false;
        if (filters.riskLevel === 'MEDIUM' && s.severity !== 3) return false;
        if (filters.riskLevel === 'LOW' && s.severity > 2) return false;
      }
      
      return true;
    });
  }, [signals, filters]);

  // Filter suppliers
  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(s => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (!s.id?.toLowerCase().includes(search) && 
            !s.name?.toLowerCase().includes(search)) {
          return false;
        }
      }
      if (filters.riskLevel && s.riskLevel !== filters.riskLevel) return false;
      return true;
    });
  }, [suppliers, filters]);

  const handleRowClick = (item, type) => {
    setSelectedItem(item);
    setDrawerType(type);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="value-chain-risk-v3">
      {/* Snapshot Lock Banner */}
      {isSnapshotLocked && (
        <SnapshotLockBanner 
          snapshotId={snapshotLockState?.snapshotId}
          lockedBy={snapshotLockState?.lockedBy}
        />
      )}

      <div className="p-6 space-y-4">
        {/* Defensibility Flywheel - Horizontal Process Layer */}
        <DefensibilityFlywheel 
          currentStep={selectedItem ? 'evidence' : 'upload'}
          context="value-chain"
          entityId={selectedItem?.id}
          compact={false}
        />

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-white">Value Chain Risk</h1>
              <p className="text-sm text-[#6B6B6B]">Risk Intelligence Layer • Cross-entity supplier exposure</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Connection Status */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
              isConnected 
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}>
              <Radio className={`w-3 h-3 ${isConnected ? 'animate-pulse' : ''}`} />
              <span className="text-xs font-medium">{mode}</span>
            </div>

            {/* Refresh */}
            <button 
              onClick={() => { fetchSignals(); fetchSuppliers(); }}
              className="p-2 hover:bg-[#1F1F1F] rounded-lg"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 text-[#6B6B6B]" />
            </button>

            {/* Export */}
            <button className="px-3 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg text-sm hover:bg-[#2F2F2F]">
              <Download className="w-4 h-4 inline mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <SystemicKPIStrip 
          signalStats={signalStats} 
          supplierStats={supplierStats}
          onKpiClick={(key) => console.log('KPI clicked:', key)}
        />

        {/* Filter Panel */}
        <FilterPanel 
          filters={filters}
          onFilterChange={setFilters}
          activeView={activeView}
          onViewChange={setActiveView}
        />

        {/* Data Grid - Conditional based on view */}
        {activeView === 'signals' ? (
          <SignalsRegistryGrid 
            signals={filteredSignals}
            loading={loading}
            onRowClick={(item) => handleRowClick(item, 'signal')}
          />
        ) : (
          <SupplierRiskGrid 
            suppliers={filteredSuppliers}
            loading={loading}
            onRowClick={(item) => handleRowClick(item, 'supplier')}
          />
        )}

        {/* Footer Info */}
        <div className="flex items-center justify-between text-xs text-[#6B6B6B]">
          <span>
            {activeView === 'signals' 
              ? `Showing ${filteredSignals.length} of ${signals.length} signals`
              : `Showing ${filteredSuppliers.length} of ${suppliers.length} suppliers`
            }
          </span>
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Detail Drawer */}
      {selectedItem && (
        <DetailDrawer 
          item={selectedItem}
          type={drawerType}
          onClose={() => { setSelectedItem(null); setDrawerType(null); }}
        />
      )}
    </div>
  );
};

export default ValueChainRiskV3;
